import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { getAuthHeaders } from '../../utils/auth';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const MIN_ZOOM = 0.75;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const PAGE_HORIZONTAL_PADDING = 24;
const OVERSCAN_PAGES = 2;
const DOCUMENT_OPTIONS = Object.freeze({
  cMapPacked: true,
});

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const touchDistance = (touches) => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
};

const PolicyTrainingPdfPage = ({
  pageNumber,
  width,
  zoom,
  isActive,
  fileName,
  onVisible,
}) => {
  const pageRef = useRef(null);

  useEffect(() => {
    const node = pageRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(pageNumber);
          }
        });
      },
      {
        root: null,
        rootMargin: '300px 0px 300px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [onVisible, pageNumber]);

  return (
    <div
      ref={pageRef}
      className="relative overflow-hidden rounded bg-white shadow-sm"
      data-page-number={pageNumber}
      style={{
        width: width * zoom,
        minHeight: width * zoom * 1.294,
      }}
    >
      {isActive ? (
        <Page
          pageNumber={pageNumber}
          width={Math.floor(width * zoom)}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          loading={(
            <div className="absolute inset-0 flex items-center justify-center bg-white text-xs text-gray-500">
              Rendering page {pageNumber}...
            </div>
          )}
          error={(
            <div className="absolute inset-0 flex items-center justify-center bg-white text-sm text-red-600">
              Unable to render page {pageNumber}.
            </div>
          )}
          canvasBackground="white"
          className="block"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-white text-xs text-gray-400">
          Page {pageNumber}
        </div>
      )}
    </div>
  );
};

const PolicyTrainingPdfViewer = ({ fileUrl, fileName }) => {
  const viewportRef = useRef(null);
  const pinchRef = useRef({ distance: 0, zoom: 1 });
  const zoomRef = useRef(1);
  const previousZoomRef = useRef(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pdfSource, setPdfSource] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [visiblePages, setVisiblePages] = useState([1]);

  zoomRef.current = zoom;

  const contentWidth = useMemo(
    () => Math.max(240, viewportWidth - PAGE_HORIZONTAL_PADDING),
    [viewportWidth],
  );

  const activePages = useMemo(() => {
    const active = new Set([1]);
    visiblePages.forEach((pageNum) => {
      for (
        let candidate = Math.max(1, pageNum - OVERSCAN_PAGES);
        candidate <= Math.min(pageCount, pageNum + OVERSCAN_PAGES);
        candidate += 1
      ) {
        active.add(candidate);
      }
    });
    return active;
  }, [pageCount, visiblePages]);

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      setPageCount(0);
      setZoom(1);
      previousZoomRef.current = 1;
      setPdfSource(null);
      setVisiblePages([1]);

      try {
        const headers = await getAuthHeaders();
        const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/attachment-file?file_url=${encodeURIComponent(fileUrl)}`;
        const response = await axios.get(proxyUrl, {
          headers,
          responseType: 'blob',
        });

        if (cancelled) return;

        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'application/pdf' });
        setPdfSource(blob);
      } catch (err) {
        console.error('Failed to load policy PDF:', err);
        if (!cancelled) {
          setError('Unable to load this PDF. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const updateWidth = () => {
      const nextWidth = viewport.clientWidth;
      if (nextWidth > 0) {
        setViewportWidth(nextWidth);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(viewport);

    return () => observer.disconnect();
  }, []);

  const adjustZoom = useCallback((delta) => {
    setZoom((current) => clampZoom(Number((current + delta).toFixed(2))));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  const handlePageVisible = useCallback((pageNum) => {
    setVisiblePages((prev) => {
      if (prev.includes(pageNum)) return prev;
      return [...prev, pageNum].sort((a, b) => a - b);
    });
  }, []);

  const handleDocumentLoadSuccess = useCallback(({ numPages }) => {
    setPageCount(numPages);
    setLoading(false);
    setError(null);
  }, []);

  const handleDocumentLoadError = useCallback((err) => {
    console.error('Failed to load policy PDF document:', err);
    setLoading(false);
    setError('Unable to load this PDF. Please try again.');
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const previousZoom = previousZoomRef.current;
    if (previousZoom === zoom) return;

    const ratio = zoom / previousZoom;
    viewport.scrollTop *= ratio;
    viewport.scrollLeft *= ratio;
    previousZoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const onTouchStart = (event) => {
      if (event.touches.length === 2) {
        pinchRef.current = {
          distance: touchDistance(event.touches),
          zoom: zoomRef.current,
        };
      }
    };

    const onTouchMove = (event) => {
      if (event.touches.length !== 2 || pinchRef.current.distance <= 0) return;
      event.preventDefault();
      const distance = touchDistance(event.touches);
      const nextZoom = clampZoom(
        (pinchRef.current.zoom * distance) / pinchRef.current.distance,
      );
      setZoom(nextZoom);
    };

    const onTouchEnd = (event) => {
      if (event.touches.length < 2) {
        pinchRef.current = { distance: 0, zoom: zoomRef.current };
      }
    };

    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [loading, error]);

  const zoomPercent = Math.round(zoom * 100);
  const pageNumbers = Array.from({ length: pageCount }, (_, index) => index + 1);

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full min-w-0">
      {!loading && !error && (
        <div className="shrink-0 flex items-center justify-between gap-2 py-2 px-1 mb-2 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-500 min-w-0 truncate hidden sm:block">
            {pageCount > 1 ? `${pageCount} pages` : 'PDF'}
          </p>
          <div className="flex items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => adjustZoom(-ZOOM_STEP)}
              disabled={zoom <= MIN_ZOOM}
              className="w-9 h-9 rounded-lg border border-gray-300 bg-white text-gray-700 font-bold text-lg leading-none touch-manipulation disabled:opacity-40"
              aria-label="Zoom out"
            >
              −
            </button>
            <span className="text-xs font-semibold text-gray-700 w-12 text-center tabular-nums">
              {zoomPercent}%
            </span>
            <button
              type="button"
              onClick={() => adjustZoom(ZOOM_STEP)}
              disabled={zoom >= MAX_ZOOM}
              className="w-9 h-9 rounded-lg border border-gray-300 bg-white text-gray-700 font-bold text-lg leading-none touch-manipulation disabled:opacity-40"
              aria-label="Zoom in"
            >
              +
            </button>
            <button
              type="button"
              onClick={resetZoom}
              disabled={zoom === 1}
              className="h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-700 text-xs font-semibold touch-manipulation disabled:opacity-40"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      <div
        ref={viewportRef}
        className="flex-1 min-h-0 h-0 overflow-auto overscroll-contain relative"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {loading && (
          <p className="absolute inset-x-0 top-8 text-center text-gray-500 text-sm z-10">Loading PDF…</p>
        )}
        {error && (
          <p className="absolute inset-x-0 top-8 text-center text-red-600 text-sm z-10">{error}</p>
        )}
        {pdfSource && (
          <div className={`mx-auto p-3 sm:p-4 ${error ? 'invisible' : ''}`}>
            <Document
              file={pdfSource}
              loading={null}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              className="flex flex-col gap-4"
              options={DOCUMENT_OPTIONS}
            >
              {pageNumbers.map((pageNumber) => (
                <PolicyTrainingPdfPage
                  key={pageNumber}
                  pageNumber={pageNumber}
                  width={contentWidth}
                  zoom={zoom}
                  isActive={activePages.has(pageNumber)}
                  fileName={fileName}
                  onVisible={handlePageVisible}
                />
              ))}
            </Document>
          </div>
        )}
      </div>

      {!loading && !error && (
        <p className="shrink-0 text-center text-xs text-gray-400 pt-2 sm:hidden">
          Pinch or use +/− to zoom · scroll to view all pages
        </p>
      )}
    </div>
  );
};

export default PolicyTrainingPdfViewer;
